import {
  Anchor,
  Button,
  FileButton,
  Group,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useState } from 'react';
import { PiPaperclip } from 'react-icons/pi';
import {
  apiErrorText,
  downloadFile,
  useAddServiceComment,
  useServiceComments,
  useServiceFiles,
  useUploadServiceFiles,
} from '@/models/clientCompanies';

const when = (iso: string) => dayjs(iso).format('DD.MM.YYYY HH:mm');

export function ServiceActivity({
  serviceId,
  canWrite,
}: {
  serviceId: number;
  canWrite: boolean;
}) {
  const comments = useServiceComments(serviceId, true);
  const files = useServiceFiles(serviceId, true);
  const addComment = useAddServiceComment(serviceId);
  const upload = useUploadServiceFiles(serviceId);
  const [body, setBody] = useState('');

  const fail = (title: string) => (e: unknown) =>
    notifications.show({ color: 'red', title, message: apiErrorText(e) });

  const post = () =>
    addComment
      .mutateAsync(body.trim())
      .then(() => setBody(''))
      .catch(fail('Comment not saved'));

  const attach = (list: File[]) =>
    list.length > 0 &&
    upload.mutateAsync(list).catch(fail('File not uploaded'));

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text fz="xs" fw={600} c="dimmed" tt="uppercase">
          Files
        </Text>
        {canWrite && (
          <FileButton onChange={attach} multiple>
            {(props) => (
              <Button
                {...props}
                size="compact-xs"
                variant="subtle"
                leftSection={<PiPaperclip size={12} />}
                loading={upload.isPending}
              >
                Attach file
              </Button>
            )}
          </FileButton>
        )}
      </Group>
      {(files.data ?? []).length === 0 && (
        <Text fz="xs" c="dimmed">
          {files.isLoading ? 'Loading...' : 'No files'}
        </Text>
      )}
      {(files.data ?? []).map((f) => (
        <Group key={f.id} gap="xs" justify="space-between" wrap="nowrap">
          <Anchor
            fz="xs"
            component="button"
            onClick={() =>
              downloadFile(
                'client-services/file-download',
                { serviceId, documentId: f.id },
                f.filename ?? `file-${f.id}`,
              ).catch(fail('Download failed'))
            }
          >
            {f.filename ?? f.name ?? `File #${f.id}`}
          </Anchor>
          <Text fz="xs" c="dimmed">
            {f.linkedByName ? `${f.linkedByName}, ` : ''}
            {when(f.linkedAt)}
          </Text>
        </Group>
      ))}

      <Text fz="xs" fw={600} c="dimmed" tt="uppercase" mt="xs">
        Comments
      </Text>
      {canWrite && (
        <Group align="flex-end" gap="xs" wrap="nowrap">
          <Textarea
            size="xs"
            placeholder="Information from the client, what was done"
            autosize
            minRows={1}
            maxLength={10000}
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            size="xs"
            onClick={post}
            disabled={!body.trim()}
            loading={addComment.isPending}
          >
            Add
          </Button>
        </Group>
      )}
      {(comments.data ?? []).length === 0 && (
        <Text fz="xs" c="dimmed">
          {comments.isLoading ? 'Loading...' : 'No comments'}
        </Text>
      )}
      {(comments.data ?? []).map((c) => (
        <Stack key={c.id} gap={0}>
          <Text fz="xs" c="dimmed">
            {c.authorName ?? c.authorEmail ?? 'System'}, {when(c.createdAt)}
          </Text>
          <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {c.body}
          </Text>
        </Stack>
      ))}
    </Stack>
  );
}
